import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "sendBtn"]
  
  connect() {
    this.messageContainer = document.getElementById("messages")
    this.queryInput = this.inputTarget
    this.sendBtn = this.sendBtnTarget
    this.history = []
    this.restoreSession()

    const newButton = document.getElementById("new-conversation")
    if (newButton) newButton.addEventListener("click", () => this.newConversation())

    this.setupComposer()

    // Handle example links
    document.querySelectorAll(".example-link").forEach(link => {
      link.addEventListener("click", (e) => this.handleExample(e))
    })
  }

  setupComposer() {
    this.queryInput.addEventListener("input", () => this.resizeInput())
    this.queryInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        this.send(event)
      }
    })
    this.resizeInput()
  }

  resizeInput() {
    const input = this.queryInput
    input.style.height = "auto"
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`
  }

  saveSession() {
    try {
      sessionStorage.setItem("docsure_chat_history", JSON.stringify(this.history))
    } catch (error) {
      // sessionStorage can be unavailable in private mode; the chat still works.
    }
  }

  restoreSession() {
    try {
      const raw = sessionStorage.getItem("docsure_chat_history")
      if (!raw) return

      const history = JSON.parse(raw)
      if (!Array.isArray(history) || history.length === 0) return

      this.history = history
      this.messageContainer.innerHTML = ""
      history.forEach((message) => {
        if (message.role === "user") {
          this.addUserMessage(message.content)
        } else {
          this.addBotMessage({ answer: message.content, sources: [] }, { restore: true })
        }
      })
    } catch (error) {
      console.warn("Could not restore chat", error)
    }
  }

  newConversation() {
    try {
      sessionStorage.removeItem("docsure_chat_history")
    } catch (error) {
      // ignore
    }
    this.history = []
    window.location.reload()
  }
  
  send(e) {
    e.preventDefault()
    
    const query = this.queryInput.value.trim()
    if (!query) return
    
    // Disable input while processing
    this.queryInput.disabled = true
    this.sendBtn.disabled = true
    
    // Clear input
    this.queryInput.value = ""
    this.resizeInput()
    
    // Add user message to chat
    this.addUserMessage(query)
    this.lastUserQuery = query
    const history = this.history.slice()
    this.history.push({ role: "user", content: query })
    
    // Show typing indicator
    this.showTypingIndicator()
    
    // Send request with the whole conversation so follow-ups keep context
    this.fetchAnswer(query, history)
  }
  
  handleExample(e) {
    e.preventDefault()
    const question = e.target.dataset.question
    this.queryInput.value = question
    this.resizeInput()
    this.send(e)
  }
  
  addUserMessage(text) {
    const messageDiv = document.createElement("div")
    messageDiv.className = "message user"
    messageDiv.innerHTML = `
      <div>
        <div class="message-bubble">${this.escapeHtml(text)}</div>
        <div class="message-meta">${this.formatTime(new Date())}</div>
      </div>
    `
    this.messageContainer.appendChild(messageDiv)
    this.scrollToBottom()
  }
  
  addBotMessage(response, { restore = false } = {}) {
    const messageDiv = document.createElement("div")
    messageDiv.className = "message bot"

    if (response.answer && !restore) {
      this.history.push({ role: "assistant", content: response.answer })
    }
    
    let html = `<div>`
    
    // Main answer only
    html += `<div class="message-bubble">`
    html += this.escapeHtml(response.answer)
    html += `</div>`
    
    // Friendly source label; no internal scores or raw source dumps.
    if (response.sources && response.sources.length > 0) {
      const src = response.sources[0]
      if (src.title) {
        html += `<div class="sources-link" style="margin-top:6px;font-size:12px;color:#888">`
        html += `Gebaseerd op: ${this.escapeHtml(src.title)}`
        html += `</div>`
      }
    }
    
    if (response.video_url) {
      html += `<div class="sources-link" style="margin-top:6px;font-size:12px">`
      html += `<a href="${this.escapeHtml(response.video_url)}" target="_blank" rel="noopener">▶ Bekijk de video</a>`
      html += `</div>`
    }

    if (response.can_escalate) {
      html += `<div style="margin-top:8px">`
      html += `<button type="button" class="escalate-btn" style="background:#1976d2;color:#fff;border:0;border-radius:4px;padding:6px 10px;font-size:12px;cursor:pointer">Vraag doorsturen</button>`
      html += `</div>`
    }

    html += `<div class="message-meta">${this.formatTime(new Date())}</div>`
    html += `</div>`
    
    messageDiv.innerHTML = html
    this.messageContainer.appendChild(messageDiv)

    const escalateButton = messageDiv.querySelector(".escalate-btn")
    if (escalateButton) {
      escalateButton.addEventListener("click", () => this.escalate(escalateButton))
    }
    
    if (!restore) this.saveSession()

    // Source citations shown inline in the answer bubble above
    this.scrollToBottom()
  }
  
  showTypingIndicator(label = "Even denken") {
    const typingDiv = document.createElement("div")
    typingDiv.className = "message bot"
    typingDiv.id = "typing-indicator"
    typingDiv.innerHTML = `
      <div class="typing-indicator">
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
        <span style="font-size:11px;color:#999;margin-left:8px">${this.escapeHtml(label)}...</span>
      </div>
    `
    this.messageContainer.appendChild(typingDiv)
    this.scrollToBottom()
  }
  
  removeTypingIndicator() {
    const typing = document.getElementById("typing-indicator")
    if (typing) typing.remove()
  }
  
  escalate(button) {
    button.disabled = true
    button.textContent = "Doorsturen..."

    fetch("/chat/escalate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ query: this.lastUserQuery || "", history: this.history, reason: "customer_request" })
    })
      .then(response => response.json())
      .then(data => {
        if (data.ok) {
          button.remove()
          this.addBotMessage({
            answer: "Je vraag is doorgestuurd naar support. Je hoort zo snel mogelijk van ons.",
            sources: []
          })
        } else {
          throw new Error("escalate failed")
        }
      })
      .catch(() => {
        button.disabled = false
        button.textContent = "Vraag doorsturen"
        this.addBotMessage({ answer: "Doorsturen lukte niet. Probeer het later opnieuw of mail info@docsure.nl.", sources: [] })
      })
  }

  fetchAnswer(query, history) {
    const url = "/chat"
    
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ query: query, history: history || [], stream: false })
    })
      .then(response => {
        if (response.status === 429) {
          this.removeTypingIndicator()
          this.addBotMessage({
            answer: "Je stuurt te veel vragen achter elkaar. Probeer het over een minuut opnieuw.",
            sources: []
          })
          return null
        }
        return response.json()
      })
      .then(data => {
        if (!data) return
        this.removeTypingIndicator()
        
        if (data.status === "success" || data.status === "fallback") {
          this.addBotMessage(data)
        } else {
          this.addBotMessage({
            answer: "Er is een fout opgetreden. Probeer het later opnieuw.",
            sources: [],
            steps: ["Fout opgetreden"]
          })
        }
      })
      .catch(error => {
        console.error("Error:", error)
        this.removeTypingIndicator()
        this.addBotMessage({
          answer: "Verbindingsfout. Controleer uw internetverbinding.",
          sources: [],
          steps: ["Verbindingsfout"]
        })
      })
      .finally(() => {
        this.queryInput.disabled = false
        this.sendBtn.disabled = false
        this.resizeInput()
        this.queryInput.focus()
      })
  }
  
  scrollToBottom() {
    this.messageContainer.scrollTop = this.messageContainer.scrollHeight
  }
  
  formatTime(date) {
    return date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })
  }
  
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
